import type { EavEntityFindInterface } from '@zaoblako/shared/modules/eav/dtos/entity/eav-entity-find.dto';
import type { EavEntityTypeFindInterface } from '@zaoblako/shared/modules/eav/dtos/entity-type/eav-entity-type-find.dto';
import type { HelpdeskTaskFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-find.dto';
import type { HelpdeskTaskTypeFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/task-type/helpdesk-task-type-find.dto';

import { isNil } from 'lodash';

export interface MergeHelpdeskTaskAfterPatchContext {
  previousTask: HelpdeskTaskFindInterface | null | undefined;
  taskTypes: HelpdeskTaskTypeFindInterface[];
  selectedTaskType?: HelpdeskTaskTypeFindInterface | null;
  taskTypeEntityTypeSnapshot?: EavEntityTypeFindInterface | null;
  taskTypeEntityTypeSnapshotTaskTypeId?: number | null;
}

const resolveTaskTypeWithEntityType = (
  taskType: HelpdeskTaskTypeFindInterface | null | undefined,
  context: MergeHelpdeskTaskAfterPatchContext
): HelpdeskTaskTypeFindInterface | null | undefined => {
  const { previousTask, taskTypes, selectedTaskType, taskTypeEntityTypeSnapshot, taskTypeEntityTypeSnapshotTaskTypeId } =
    context;
  if (isNil(taskType)) {
    return taskType;
  }
  if ((taskType.entityType?.attributes?.length ?? 0) > 0) {
    return taskType;
  }
  const fromSelected =
    selectedTaskType?.id === taskType.id && (selectedTaskType?.entityType?.attributes?.length ?? 0) > 0
      ? selectedTaskType
      : undefined;
  if (fromSelected) {
    return { ...taskType, entityType: fromSelected.entityType };
  }
  const fromTaskTypes = taskTypes.find(({ id }) => id === taskType.id);
  if ((fromTaskTypes?.entityType?.attributes?.length ?? 0) > 0) {
    return { ...taskType, entityType: fromTaskTypes.entityType };
  }
  if (
    previousTask?.taskType?.id === taskType.id &&
    (previousTask.taskType?.entityType?.attributes?.length ?? 0) > 0
  ) {
    return { ...taskType, entityType: previousTask.taskType.entityType };
  }
  if (
    taskTypeEntityTypeSnapshotTaskTypeId === taskType.id &&
    !isNil(taskTypeEntityTypeSnapshot) &&
    (taskTypeEntityTypeSnapshot.attributes?.length ?? 0) > 0
  ) {
    return { ...taskType, entityType: taskTypeEntityTypeSnapshot };
  }
  return taskType;
};

const mergeEavEntityAfterPatch = (
  previous: EavEntityFindInterface | undefined,
  patched: EavEntityFindInterface | undefined
): EavEntityFindInterface | undefined => {
  if (isNil(patched)) {
    return previous;
  }
  if (isNil(previous)) {
    return patched;
  }
  return {
    ...previous,
    ...patched,
    values: {
      ...previous.values,
      ...patched.values
    }
  };
};

/** CRM/LK card v2: PATCH often omits `taskType.entityType` — keep EAV block visible. */
export const mergeHelpdeskTaskAfterPatch = (
  patched: HelpdeskTaskFindInterface,
  context: MergeHelpdeskTaskAfterPatchContext
): HelpdeskTaskFindInterface => {
  const { previousTask } = context;
  if (isNil(previousTask) || patched.id !== previousTask.id) {
    return patched;
  }
  const taskType = resolveTaskTypeWithEntityType(patched.taskType ?? previousTask.taskType, context);
  const eavEntity = mergeEavEntityAfterPatch(previousTask.eavEntity, patched.eavEntity);
  return {
    ...previousTask,
    ...patched,
    taskType,
    eavEntity
  };
};

export const rememberHelpdeskTaskTypeEntityTypeSnapshot = (
  task: HelpdeskTaskFindInterface
): { entityType: EavEntityTypeFindInterface; taskTypeId: number; } | null => {
  const taskTypeId = task.taskType?.id;
  const entityType = task.taskType?.entityType;
  if (isNil(taskTypeId) || (entityType?.attributes?.length ?? 0) === 0) {
    return null;
  }
  return { entityType, taskTypeId };
};
